/**
 * This person's choice for a second call during a call, kept fresh.
 *
 * Read once when the call UI starts, and again whenever it may have changed:
 * the Settings card posts the new value on a BroadcastChannel (which reaches
 * this window AND the other CRM tab that may be the one holding the phone),
 * and a window coming back into focus asks again in case that message was
 * missed. The decision itself is made from the last value read, because an
 * offer has to be answered or turned down without waiting on a request.
 *
 * A failed read keeps the last value, and before the first read lands the
 * value is the default (`knock`), the same answer the server gives a person
 * who never chose.
 */
import { useCallback, useEffect, useRef } from "react";
import { BUSY_MODE_CHANNEL, getBusyMode } from "../../../lib/callBusyMode";
import { DEFAULT_BUSY_MODE, parseBusyMode, type BusyMode } from "./busyMode";

/** Narrowed here as well: a value this build does not know is the default. */
function fetchBusyMode(): Promise<BusyMode> {
  return getBusyMode().then((res) => parseBusyMode(res?.busyMode));
}

/** A reader for the current choice. A function, so a handler reads it fresh. */
export function useBusyMode(enabled: boolean): () => BusyMode {
  const modeRef = useRef<BusyMode>(DEFAULT_BUSY_MODE);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const refresh = () => {
      void fetchBusyMode()
        .then((mode) => {
          if (alive) modeRef.current = mode;
        })
        .catch(() => {
          // Keep the last value. A missed read must not flip the behaviour.
        });
    };
    refresh();

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(BUSY_MODE_CHANNEL);
      channel.onmessage = (e: MessageEvent) => {
        // The value travels with the message, but it is read again anyway:
        // the server's answer is the one every device shares.
        if (alive) modeRef.current = parseBusyMode(e.data);
        refresh();
      };
    } catch {
      // No BroadcastChannel in this browser. Focus below still catches up.
    }
    window.addEventListener("focus", refresh);
    return () => {
      alive = false;
      channel?.close();
      window.removeEventListener("focus", refresh);
    };
  }, [enabled]);

  return useCallback(() => modeRef.current, []);
}
